import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fmtMoney, imgSrc } from "../lib/api";

const CITY_COORDS = {
  Portland: [45.5152, -122.6784], Salem: [44.9429, -123.0351], Eugene: [44.0521, -123.0868], Bend: [44.0582, -121.3153],
  Medford: [42.3265, -122.8756], Hillsboro: [45.5229, -122.9898], Beaverton: [45.4871, -122.8037], Gresham: [45.4982, -122.4302],
  Corvallis: [44.5646, -123.262], Tigard: [45.4312, -122.7715], "Lake Oswego": [45.4207, -122.6706], Albany: [44.6365, -123.1059],
  "Grants Pass": [42.439, -123.3284], "Klamath Falls": [42.2249, -121.7817],
};

const jitter = (id, salt) => {
  let h = 0;
  for (const c of id + salt) h = (h * 31 + c.charCodeAt(0)) | 0;
  return ((Math.abs(h) % 1000) / 1000 - 0.5) * 0.03;
};

const position = (p) => {
  if (p.lat != null && p.lng != null) return [p.lat, p.lng];
  const c = CITY_COORDS[p.city];
  return c ? [c[0] + jitter(p.id, "a"), c[1] + jitter(p.id, "b")] : null;
};

const short = (v) => (!v ? "TBD" : v >= 1e6 ? `$${(v / 1e6).toFixed(v >= 1e7 ? 0 : 2).replace(/\.?0+$/, "")}M` : `$${Math.round(v / 1000)}K`);

const pinIcon = (p) =>
  L.divIcon({
    className: "",
    iconSize: null,
    html: `<div class="sido-pin${p.category === "distressed" ? " sido-pin--distressed" : ""}">${short(p.asking_price || p.est_acquisition_price)}</div>`,
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [60, 60], maxZoom: 10 });
    else if (points.length === 1) map.setView(points[0], 11);
  }, [map, points]);
  return null;
}

export default function MapView({ items }) {
  const pins = items.map((p) => ({ p, pos: position(p) })).filter((x) => x.pos);
  return (
    <div data-testid="map-view">
      <div className="relative h-[480px] sm:h-[600px] border border-navy/10 bg-white overflow-hidden">
        <MapContainer center={[44.0, -120.6]} zoom={7} scrollWheelZoom={false} className="h-full w-full">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="sido-tiles"
          />
          <FitBounds points={pins.map((x) => x.pos)} />
          {pins.map(({ p, pos }) => (
            <Marker key={p.id} position={pos} icon={pinIcon(p)}>
              <Popup minWidth={240}>
                <div className="w-[240px]" data-testid={`map-popup-${p.slug}`}>
                  <img src={imgSrc(p.images?.[0])} alt="" className="h-28 w-full object-cover" />
                  <div className="p-3">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-mist">{p.property_type} · {p.status}</p>
                    <p className="mt-1 font-serif text-lg leading-tight text-navy">{p.title}</p>
                    <p className="text-xs text-mist">{p.city}, Oregon</p>
                    <p className="metric mt-2 text-sm">{fmtMoney(p.asking_price || p.est_acquisition_price) || "Price on request"}</p>
                    <Link to={`/opportunities/${p.slug}`} className="mt-3 block bg-navy py-2 text-center text-xs font-semibold !text-paper" data-testid={`map-popup-link-${p.slug}`}>View Opportunity</Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <p className="mt-3 text-xs text-mist">Pins show approximate, city-level locations. Exact addresses are shared with approved investors.</p>
    </div>
  );
}
